import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, CheckCircle2, ShieldCheck } from "lucide-react";

const PRESETS = [
  { name: "Gmail", host: "smtp.gmail.com", port: 587, use_tls: true },
  { name: "Outlook", host: "smtp.office365.com", port: 587, use_tls: true },
  { name: "iCloud", host: "smtp.mail.me.com", port: 587, use_tls: true },
  { name: "Fastmail", host: "smtp.fastmail.com", port: 587, use_tls: true },
  { name: "Yahoo", host: "smtp.mail.yahoo.com", port: 587, use_tls: true },
];

export default function SmtpSettings() {
  const [host, setHost] = useState("");
  const [port, setPort] = useState(587);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [useTls, setUseTls] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [savedCfg, setSavedCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/smtp/config").then((r) => {
      const c = r.data.config;
      if (c) { setSavedCfg(c); setHost(c.host); setPort(c.port); setEmail(c.email); setUseTls(c.use_tls); setDisplayName(c.display_name || ""); }
    });
  }, []);

  const applyPreset = (p) => { setHost(p.host); setPort(p.port); setUseTls(p.use_tls); toast.info(`Preset applied: ${p.name}`); };

  const save = async () => {
    if (!host || !email || !password) return toast.error("Fill host, email and password");
    setSaving(true);
    try {
      await api.post("/smtp/config", { host, port: Number(port), email, password, use_tls: useTls, display_name: displayName });
      toast.success("Saved");
      setSavedCfg({ host, port: Number(port), email, use_tls: useTls, display_name: displayName });
      setPassword("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post("/smtp/test"); toast.success(data.message || "Connection successful"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-8">
          <div className="overline">Email setup</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Connect your inbox</h1>
          <p className="text-sm text-[#5C6661] mt-2 max-w-2xl">JobKnocker sends applications through your own SMTP server so replies come straight to you. Your password is encrypted at rest.</p>
        </div>

        {savedCfg && (
          <Card className="p-4 border-[#E2E8E4] bg-[#E7F0EB]/40 mb-6 flex items-center justify-between" data-testid="smtp-status">
            <div className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#2D4A3E]" />
              <div className="text-sm">
                <div className="font-medium text-[#121413]">Inbox connected · {savedCfg.email}</div>
                <div className="text-xs text-[#5C6661]">{savedCfg.host}:{savedCfg.port} · TLS {savedCfg.use_tls ? "on" : "off"}</div>
              </div>
            </div>
            <Button onClick={test} disabled={testing} variant="outline" size="sm" className="rounded-full border-[#2D4A3E]/30" data-testid="test-smtp-btn"><ShieldCheck className="w-3.5 h-3.5 mr-2" /> {testing ? "Testing…" : "Test connection"}</Button>
          </Card>
        )}

        <div className="mb-6">
          <Label className="overline">Quick presets</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button key={p.name} onClick={() => applyPreset(p)} variant="outline" size="sm" className="rounded-full border-[#E2E8E4]" data-testid={`preset-${p.name.toLowerCase()}`}>{p.name}</Button>
            ))}
          </div>
        </div>

        <Card className="p-6 border-[#E2E8E4]">
          <div className="grid md:grid-cols-2 gap-5">
            <div><Label className="text-sm">SMTP host</Label>
              <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.gmail.com" className="mt-1.5 h-11 rounded-lg" data-testid="smtp-host" /></div>
            <div><Label className="text-sm">Port</Label>
              <Input type="number" value={port} onChange={(e) => setPort(e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="smtp-port" /></div>
            <div><Label className="text-sm">Email address</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourdomain.com" className="mt-1.5 h-11 rounded-lg" data-testid="smtp-email" /></div>
            <div><Label className="text-sm">App password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={savedCfg ? "Enter new password to update" : "SMTP password / app password"} className="mt-1.5 h-11 rounded-lg" data-testid="smtp-password" /></div>
            <div><Label className="text-sm">Display name (optional)</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 h-11 rounded-lg" data-testid="smtp-display-name" /></div>
            <div className="flex items-center justify-between rounded-lg border border-[#E2E8E4] px-4">
              <div><div className="text-sm font-medium">Use STARTTLS</div><div className="text-xs text-[#5C6661]">Recommended</div></div>
              <Switch checked={useTls} onCheckedChange={setUseTls} data-testid="smtp-tls-switch" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={save} disabled={saving} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="save-smtp-btn">{saving ? "Saving…" : (savedCfg ? "Update settings" : "Save & connect")}</Button>
            {savedCfg && <Button onClick={test} disabled={testing} variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="test-smtp-inline"><Mail className="w-4 h-4 mr-2" /> {testing ? "Testing…" : "Test connection"}</Button>}
          </div>
        </Card>

        <div className="mt-6 text-xs text-[#5C6661] leading-relaxed max-w-2xl">
          <strong>Gmail tip:</strong> Enable 2-Step Verification and create an App Password at <span className="font-mono">myaccount.google.com/apppasswords</span>. Paste the 16-character password above.
        </div>
      </main>
    </div>
  );
}
